import { ToastrService } from 'ngx-toastr';
import { Component, Input, OnInit, OnDestroy, Output, EventEmitter } from '@angular/core';
import { ViewUpdateTprApiService } from '../../../../../../modules/api/view-update-tpr-api.service';
import { MatLegacyCheckboxChange as MatCheckboxChange, MatLegacyCheckbox as MatCheckbox } from '@angular/material/legacy-checkbox';
import { defaultIfEmpty, map, finalize, filter } from 'rxjs/operators';
import { ViewUpdateTpr } from '../../view-update-tpr.class';
import { TprecmAuthorizationService } from '../../../authorization/tprecm-authorization.service';

const DEFAULT_MAX_VALUE = 1;

@Component({
    selector: 'tpr-design-progress',
    templateUrl: './tpr-design-progress.template.html',
    styleUrls: ['../../../tprecm.style.scss', './tpr-design-progress.scss']
})
export class TprDesignProgressComponent {

    disabled = false;
    _selectedTpr: ViewUpdateTpr;

    get selectedTpr(): ViewUpdateTpr {
        return this._selectedTpr;
    }

    @Input()
    set selectedTpr(value: ViewUpdateTpr) {
        this.disabled = false;
        if (value) {
            this._selectedTpr = value;
        } else {
            this.disabled = true;
            this._selectedTpr = new ViewUpdateTpr();
        }
    }

    @Input()
    reSourced = false;

    @Input()
    inActive = false;

    spanOpts: any[] = ['K', 'L', 'Z'];
    daysOpts: any[] = Array(41).fill(0).map((_, index) => -20 + index);

    @Output()
    updateEvent = new EventEmitter<any>();

    constructor(
        private readonly viewUpdateTprApiService: ViewUpdateTprApiService,
        private readonly toastr: ToastrService,
        private readonly auth: TprecmAuthorizationService
    ) { }

    onModelPercChange(checkbox: MatCheckbox) {
        if (!this.inputRangeCheck(this.selectedTpr.modelPerc, 'Model Percentage')) {
            return;
        }
        checkbox.checked = this.selectedTpr.modelPerc === 100 ? checkbox.checked : false;
        this.onGenericFieldChange('model', Math.round(this.selectedTpr.modelPerc) / 100, true);
    }

    inputRangeCheck(inputData: number, field: string) {
        if (!(inputData >= 0 && inputData <= 100)) {
            this.toastr.error('Valid range is between 0 and 100 for ' + field);
            return false;
        }
        return true;
    }

    onMathPercChange(checkbox: MatCheckbox) {
        if (!this.inputRangeCheck(this.selectedTpr.mathPerc, 'Math Percentage')) {
            return;
        }
        checkbox.checked = this.selectedTpr.mathPerc === 100 ? checkbox.checked : false;
        this.onGenericFieldChange('math', Math.round(this.selectedTpr.mathPerc) / 100, true);
    }

    onLOPercChange(checkbox: MatCheckbox) {
        if (!this.inputRangeCheck(this.selectedTpr.detail1Perc, 'Detail 1 Percentage')) {
            return;
        }
        checkbox.checked = this.selectedTpr.detail1Perc === 100 ? checkbox.checked : false;
        this.onGenericFieldChange('detail1', Math.round(this.selectedTpr.detail1Perc) / 100, true);
    }

    onCheckPercChange(checkbox: MatCheckbox) {
        if (!this.inputRangeCheck(this.selectedTpr.checkedPerc, 'Checked Percentage')) {
            return;
        }
        checkbox.checked = this.selectedTpr.checkedPerc === 100 ? checkbox.checked : false;
        this.onGenericFieldChange('checked', Math.round(this.selectedTpr.checkedPerc) / 100, true);
    }

    onOpsPercChange(checkbox: MatCheckbox) {
        if (!this.inputRangeCheck(this.selectedTpr.opsPerc, 'Ops Percentage')) {
            return;
        }
        checkbox.checked = this.selectedTpr.opsPerc === 100 ? checkbox.checked : false;
        this.onGenericFieldChange('ops', Math.round(this.selectedTpr.opsPerc) / 100, true);
    }

    onModelCheckChange(event: MatCheckboxChange) {
        if (event.checked) {
            this.selectedTpr.modelPerc = DEFAULT_MAX_VALUE * 100;
            this.onGenericFieldChange('model', DEFAULT_MAX_VALUE, true);
        }
    }

    onMathCheckChange(event: MatCheckboxChange) {
        if (event.checked) {
            this.selectedTpr.mathPerc = DEFAULT_MAX_VALUE * 100;
            this.onGenericFieldChange('math', DEFAULT_MAX_VALUE, true);
        }
    }

    onLOCheckChange(event: MatCheckboxChange) {
        if (event.checked) {
            this.selectedTpr.detail1Perc = DEFAULT_MAX_VALUE * 100;
            this.onGenericFieldChange('detail1', DEFAULT_MAX_VALUE, true);
        }
    }

    onCheckCheckChange(event: MatCheckboxChange) {
        if (event.checked) {
            this.selectedTpr.checkedPerc = DEFAULT_MAX_VALUE * 100;
            this.onGenericFieldChange('checked', DEFAULT_MAX_VALUE, true);
        }
    }

    onOpsCheckChange(event: MatCheckboxChange) {
        if (event.checked) {
            this.selectedTpr.opsPerc = DEFAULT_MAX_VALUE * 100;
            this.onGenericFieldChange('ops', DEFAULT_MAX_VALUE, true);
        }
    }

    onActualFinishChanged() {
        const start = new Date(this.selectedTpr.designActualStart);
        start.setHours(0, 0, 0);

        if (this.selectedTpr.designActualFinish && this.selectedTpr.designActualFinish < start) {
            this.toastr.error('Date not saved. The Actual Date must be greater than or equal to the Actual Start date.');
            return;
        }

        this.onGenericFieldChange('designActualFinish', this.selectedTpr.designActualFinish || '', true);
    }

    fieldAuthorized(field: string) {
        return this.auth.getAuthorizationByField(field);
    }

    onGenericFieldChange(fieldName: string, fieldValue: any, calcFields = false) {
        this.disabled = true;
        const params = {
            toolId: this.selectedTpr.toolId,
            tprToolId: this.selectedTpr.tprToolId,
            calcFieldsRequired: calcFields,
            fieldName,
            fieldValue
        };
        this.viewUpdateTprApiService.updateGenericTprField(params)
            .pipe(
                filter(Boolean),
                defaultIfEmpty({ data: [], totalCount: 0 }),
                map((response: any) => response.data[0]),
                finalize(() => this.disabled = false)
            ).subscribe((updatedTpr: any) => this.updateEvent.emit(updatedTpr));
    }

    actualFinishEnabled() {
        return [
            this.selectedTpr.modelPerc,
            this.selectedTpr.mathPerc,
            this.selectedTpr.detail1Perc,
            this.selectedTpr.checkedPerc,
            this.selectedTpr.opsPerc
        ].every(val => {
            return val === 100;
        });
    }

    getEtac() {
        return [
            'ETAC Inside',
            'CTAC',
            'Mini ETAC',
            'ETAC',
            'ITAC'
        ].some(val => val === this.selectedTpr.typeName);
    }

    validInput(data: number) {
        return data >= 0 && data <= 100 ? true : false;
    }
}
